import { Box, Divider, Loader, Notice, Stack, Text } from 'braid-design-system';
import React, {
  type ComponentProps,
  type ForwardedRef,
  forwardRef,
  useEffect,
  useRef,
} from 'react';

import { ScrollableCard } from '../ScrollableCard/ScrollableCard.tsx';

interface LoadMoreProps {
  shouldLoadMore: boolean;
  errorMessage?: string;
  subject?: string;
}

const LoadMore = forwardRef(
  (props: LoadMoreProps, ref: ForwardedRef<HTMLElement>) => {
    const { errorMessage, shouldLoadMore, subject } = props;
    const content = subject ?? 'items';

    if (errorMessage) {
      return (
        <Notice tone="critical">
          <Stack space="small">
            <Text>We couldn’t load more {content}.</Text>
            <Text size="small">{errorMessage}</Text>
          </Stack>
        </Notice>
      );
    }

    if (shouldLoadMore) {
      return (
        <Box display="flex" justifyContent="center" ref={ref} width="full">
          <Loader />
        </Box>
      );
    }

    return <Text tone="secondary">No more {content} to show.</Text>;
  },
);

interface InfiniteScrollingListProps {
  items: Array<ComponentProps<typeof Stack>['children']>;
  loadMore: () => Promise<unknown>;
  shouldLoadMore: boolean;
  isEmpty: boolean;
  /**
   * The pluralised name of items in the list, e.g. `events`.
   */
  subject?: string;
  dividers?: boolean;
  spacing?: boolean;
  Wrapper?: React.ComponentType;
  errorMessage?: string;
}

export const InfiniteScrollingList = (props: InfiniteScrollingListProps) => {
  const {
    items,
    loadMore,
    shouldLoadMore,
    isEmpty,
    subject,
    dividers = true,
    spacing = true,
    Wrapper = ScrollableCard,
    errorMessage = '',
  } = props;

  const loadMoreRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const options = {
      rootMargin: '0px',
      threshold: [0.1],
    };

    const observer = new IntersectionObserver(async (entries) => {
      const { isIntersecting } = entries[0];

      if (isIntersecting && shouldLoadMore && !errorMessage) {
        await loadMore();
      }
    }, options);

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loadMore, errorMessage, shouldLoadMore]);

  if (isEmpty) {
    return <Text tone="secondary">No {subject} found.</Text>;
  }

  return (
    <Wrapper>
      <Stack space={spacing ? 'medium' : 'none'}>
        {React.Children.map(items, (item, index) => (
          <React.Fragment key={index}>
            {dividers && index > 0 && <Divider />}
            {item}
          </React.Fragment>
        ))}

        {dividers && <Divider />}

        <Box paddingTop={spacing ? 'none' : 'medium'}>
          <LoadMore
            ref={loadMoreRef}
            errorMessage={errorMessage}
            shouldLoadMore={shouldLoadMore}
            subject={subject}
          />
        </Box>
      </Stack>
    </Wrapper>
  );
};
